import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack,
  LinearProgress, Divider, Skeleton, Alert, useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { billingApi } from "../../api/billing.js";
import { GlowButton } from "../../components/GlowButton.js";

const CHUNKS_PER_DOC = 50; // avg 10-page PDF

interface BillingInfo {
  tier: string;
  messages_used: number;
  messages_limit: number;
  overage_credits: number;
  billing_period_start?: string;
  billing_period_end?: string;
  tokens_used_input: number;
  tokens_used_output: number;
  chunks_stored: number;
  chunks_limit: number;
}

const PLANS = [
  {
    key:      "free",
    name:     "Free",
    price:    "$0",
    period:   "forever",
    features: ["1 bot", "250 messages / mo", "~10 documents (500 chunks)", "Community support"],
  },
  {
    key:      "starter_monthly",
    name:     "Starter",
    price:    "$10",
    period:   "/ month",
    features: ["5 bots", "2,500 messages / mo", "~500 documents (25,000 chunks)", "Custom slug", "Email support"],
    highlight: true,
  },
  {
    key:      "pro_monthly",
    name:     "Pro",
    price:    "$35",
    period:   "/ month",
    features: ["20 bots", "10,000 messages / mo", "~2,000 documents (100,000 chunks)", "Overage credits", "Priority support"],
  },
];

function PlanCard({
  plan, planIndex, currentIndex, current, onSelect, loadingKey,
}: {
  plan: typeof PLANS[number];
  planIndex: number;
  currentIndex: number;
  current: boolean;
  onSelect: (key: string) => void;
  loadingKey: string | null;
}) {
  const theme = useTheme();
  const isUpgrade = planIndex > currentIndex;
  const isDowngrade = planIndex < currentIndex;
  const accentBorder = `2px solid ${theme.palette.primary.main}`;
  const label = loadingKey === plan.key
    ? "Redirecting…"
    : `${isDowngrade ? "Downgrade to" : "Upgrade to"} ${plan.name}`;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        // Only highlight current plan or upgrades — never downgrades
        border: (current || isUpgrade) ? accentBorder : undefined,
        position: "relative",
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        <Typography variant="h5" mb={0.5}>{plan.name}</Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.5} mb={2}>
          <Typography variant="h2" sx={{ fontWeight: 800 }}>{plan.price}</Typography>
          <Typography variant="body2" color="text.secondary">{plan.period}</Typography>
        </Stack>
        <Stack spacing={1} mb={3}>
          {plan.features.map(f => (
            <Stack key={f} direction="row" alignItems="center" spacing={1}>
              <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
              <Typography variant="body2">{f}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        {current ? (
          <Button fullWidth variant="outlined" disabled>Current plan</Button>
        ) : isUpgrade ? (
          <GlowButton onClick={() => onSelect(plan.key)} disabled={!!loadingKey} loading={loadingKey === plan.key} size="md">
            {label}
          </GlowButton>
        ) : (
          // Downgrade — plain muted button, no enticement
          <Button fullWidth variant="text" onClick={() => onSelect(plan.key)} disabled={!!loadingKey}
            sx={{ color: "text.disabled", "&:hover": { color: "text.secondary" } }}>
            {label}
          </Button>
        )}
      </Box>
    </Card>
  );
}

function UsageRow({
  label, used, limit, unit, color,
}: {
  label: string;
  used: number;
  limit: number;
  unit?: string;
  color?: "primary" | "warning" | "error";
}) {
  const pct = limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 0;
  const barColor = color ?? (pct >= 90 ? "error" : pct >= 80 ? "warning" : "primary");
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {used.toLocaleString()}{unit ? ` ${unit}` : ""} / {limit.toLocaleString()}{unit ? ` ${unit}` : ""}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate" value={pct}
        color={barColor}
        sx={{ borderRadius: 2, height: 6 }}
      />
    </Stack>
  );
}

export function BillingPage() {
  const [info, setInfo]         = useState<BillingInfo | null>(null);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [notice, setNotice]     = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutStatus = searchParams.get("checkout");

  useEffect(() => {
    billingApi.get().then(setInfo).catch(() => setError("Failed to load billing info"));
  }, []);

  // Handle Stripe checkout return. On success, Stripe confirms asynchronously
  // via webhook — so we poll the billing endpoint until the tier updates
  // (or give up after ~30s and show a soft message). On cancel, just show
  // a friendly notice without polling.
  useEffect(() => {
    if (!checkoutStatus) return;

    if (checkoutStatus === "cancelled") {
      setNotice("Checkout cancelled. You have not been charged.");
      setSearchParams({}, { replace: true });
      return;
    }

    if (checkoutStatus !== "success") return;

    setNotice("Finalising your subscription — this usually takes a few seconds…");

    // Poll billing until tier leaves "free", capped at ~30s.
    let attempts = 0;
    const maxAttempts = 15;
    const poll = async () => {
      attempts += 1;
      try {
        const next = await billingApi.get();
        setInfo(next);
        if (next.tier !== "free") {
          setNotice(`You're now on the ${next.tier.charAt(0).toUpperCase() + next.tier.slice(1)} plan. Welcome!`);
          setSearchParams({}, { replace: true });
          return;
        }
      } catch { /* transient — retry */ }
      if (attempts >= maxAttempts) {
        setNotice("Your payment is processing. Refresh this page in a moment to see your new plan.");
        setSearchParams({}, { replace: true });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [checkoutStatus, setSearchParams]);

  const upgrade = async (priceKey: string) => {
    setLoadingKey(priceKey);
    try {
      const { checkout_url } = await billingApi.checkout(priceKey);
      window.location.href = checkout_url;
    } catch {
      setError("Could not start checkout — try again.");
      setLoadingKey(null);
    }
  };

  const portal = async () => {
    setLoadingKey("portal");
    try {
      const { portal_url } = await billingApi.portal();
      window.location.href = portal_url;
    } catch {
      setError("Could not open billing portal — try again.");
      setLoadingKey(null);
    }
  };

  const chunkPct = info && info.chunks_limit > 0
    ? Math.round((info.chunks_stored / info.chunks_limit) * 100) : 0;
  const docsEquiv = info ? Math.floor(info.chunks_stored / CHUNKS_PER_DOC) : 0;
  const docsLimit = info ? Math.floor(info.chunks_limit / CHUNKS_PER_DOC) : 0;
  const chunksOverLimit = info ? info.chunks_stored > info.chunks_limit : false;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h2">Billing</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage your subscription and usage.
          </Typography>
        </Box>
        {info && info.tier !== "free" && (
          <Button variant="outlined" onClick={portal} disabled={loadingKey === "portal"}>
            {loadingKey === "portal" ? "Opening…" : "Manage Subscription"}
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity={checkoutStatus === "cancelled" ? "info" : "success"} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {chunksOverLimit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You've exceeded your storage quota. New documents cannot be ingested until you delete some content or upgrade your plan.
        </Alert>
      )}

      {/* Usage summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={2.5}>Usage this period</Typography>
          {!info ? (
            <Stack spacing={2}>
              <Skeleton variant="text" width="60%" /><Skeleton variant="rounded" height={6} />
              <Skeleton variant="text" width="60%" /><Skeleton variant="rounded" height={6} />
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <UsageRow
                label="Messages"
                used={info.messages_used}
                limit={info.messages_limit}
              />
              <UsageRow
                label={`Storage — ${info.chunks_stored.toLocaleString()} chunks stored (~${docsEquiv} docs)`}
                used={info.chunks_stored}
                limit={info.chunks_limit}
                color={chunkPct >= 95 ? "error" : chunkPct >= 80 ? "warning" : "primary"}
              />
              <Stack direction="row" spacing={4} pt={0.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Input tokens</Typography>
                  <Typography variant="h5">{info.tokens_used_input.toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Output tokens</Typography>
                  <Typography variant="h5">{info.tokens_used_output.toLocaleString()}</Typography>
                </Box>
                {info.billing_period_end && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Messages reset</Typography>
                    <Typography variant="h5">{new Date(info.billing_period_end).toLocaleDateString()}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Storage capacity</Typography>
                  <Typography variant="h5">~{docsLimit} docs / {info.chunks_limit.toLocaleString()} chunks</Typography>
                </Box>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ mb: 3 }} />
      <Typography variant="h4" mb={2}>Plans</Typography>

      <Grid container spacing={2}>
        {PLANS.map((plan, i) => (
          <Grid item xs={12} md={4} key={plan.key}>
            <PlanCard
              plan={plan}
              planIndex={i}
              currentIndex={info ? PLANS.findIndex(p => p.key.startsWith(info.tier)) : 0}
              current={!!info && plan.key.startsWith(info.tier)}
              onSelect={upgrade}
              loadingKey={loadingKey}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
